import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import api from '../../services/api';
import { showSuccess, showError, showConfirmation } from '../../services/alert';

const emptyForm = {
  safraId: '',
  insumoId: '',
  talhaoId: '',
  maquinaId: '',
  quantidade: '',
  dataAplicacao: new Date().toISOString().substring(0, 10),
  operador: '',
  observacao: ''
};

const AplicacaoInsumoList = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [aplicacoes, setAplicacoes] = useState([]);
  const [safras, setSafras] = useState([]);
  const [insumos, setInsumos] = useState([]);
  const [talhoes, setTalhoes] = useState([]);
  const [maquinas, setMaquinas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const carregarAplicacoes = async () => {
    setLoading(true);

    try {
      const response = await api.get('/aplicacoesinsumo');
      setAplicacoes(response.data);
    } catch {
      showError('Erro ao carregar aplicações de insumo.');
    } finally {
      setLoading(false);
    }
  };

  const carregarSelects = async () => {
    try {
      const [resSafras, resInsumos, resTalhoes, resMaquinas] = await Promise.all([
        api.get('/safras'),
        api.get('/insumos'),
        api.get('/talhoes'),
        api.get('/maquinas'),
      ]);

      setSafras(resSafras.data);
      setInsumos(resInsumos.data);
      setTalhoes(resTalhoes.data);
      setMaquinas(resMaquinas.data);
    } catch {
      showError('Erro ao carregar listas auxiliares.');
    }
  };

  useEffect(() => {
    carregarSelects();
    carregarAplicacoes();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    const payload = {
      ...form,
      safraId: Number(form.safraId),
      insumoId: Number(form.insumoId),
      talhaoId: form.talhaoId ? Number(form.talhaoId) : null,
      maquinaId: form.maquinaId ? Number(form.maquinaId) : null,
      quantidade: Number(form.quantidade)
    };

    try {
      await api.post('/aplicacoesinsumo', payload);
      showSuccess('Aplicação registrada e estoque atualizado.');
      setForm(emptyForm);
      await carregarAplicacoes();
    } catch (error) {
      const message =
        error.response?.data?.message ||
        'Erro ao salvar aplicação de insumo.';

      showError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmado = await showConfirmation(
      'Deseja realmente excluir esta aplicação de insumo?'
    );

    if (!confirmado) return;

    try {
      await api.delete(`/aplicacoesinsumo/${id}`);
      showSuccess('Aplicação de insumo excluída com sucesso.');
      carregarAplicacoes();
    } catch (error) {
      const message =
        error.response?.data?.message ||
        'Erro ao excluir aplicação de insumo.';

      showError(message);
    }
  };

  return (
    <div className="container mt-4">
      <Header />

      <div className="card shadow-lg border-0 rounded-3 mb-4">
        <div className="card-header bg-success text-white d-flex justify-content-between align-items-center rounded-top-3">
          <div>
            <h5 className="mb-0">Aplicações no Campo</h5>
            <small className="text-white-50">Safra, talhão, produto aplicado e baixa automática do estoque</small>
          </div>

          <button
            type="button"
            className="btn btn-light btn-sm text-success fw-semibold"
            onClick={() => navigate('/menu')}
          >
            <i className="bi bi-arrow-left me-1" />
            Voltar
          </button>
        </div>

        <div className="card-body">
          <form className="row g-3" onSubmit={handleSubmit}>
            <div className="col-lg-4 col-md-6">
              <label className="form-label fw-semibold">Safra *</label>
              <select
                name="safraId"
                value={form.safraId}
                onChange={handleChange}
                className="form-select"
                required
                disabled={saving}
              >
                <option value="">Selecione...</option>
                {safras.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.descricao || s.cultura || `Safra ${s.id}`}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-lg-4 col-md-6">
              <label className="form-label fw-semibold">Insumo *</label>
              <select
                name="insumoId"
                value={form.insumoId}
                onChange={handleChange}
                className="form-select"
                required
                disabled={saving}
              >
                <option value="">Selecione...</option>
                {insumos.map(i => (
                  <option key={i.id} value={i.id}>
                    {i.nome} {i.estoqueAtual !== undefined ? `- Estoque: ${i.estoqueAtual}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-lg-4 col-md-6">
              <label className="form-label fw-semibold">Talhão</label>
              <select
                name="talhaoId"
                value={form.talhaoId}
                onChange={handleChange}
                className="form-select"
                disabled={saving}
              >
                <option value="">Selecione...</option>
                {talhoes.map(t => (
                  <option key={t.id} value={t.id}>{t.nome}</option>
                ))}
              </select>
            </div>

            <div className="col-lg-3 col-md-6">
              <label className="form-label fw-semibold">Quantidade *</label>
              <input
                type="number"
                name="quantidade"
                value={form.quantidade}
                onChange={handleChange}
                className="form-control"
                min="0.01"
                step="0.01"
                required
                disabled={saving}
              />
            </div>

            <div className="col-lg-3 col-md-6">
              <label className="form-label fw-semibold">Data *</label>
              <input
                type="date"
                name="dataAplicacao"
                value={form.dataAplicacao}
                onChange={handleChange}
                className="form-control"
                required
                disabled={saving}
              />
            </div>

            <div className="col-lg-3 col-md-6">
              <label className="form-label fw-semibold">Máquina</label>
              <select
                name="maquinaId"
                value={form.maquinaId}
                onChange={handleChange}
                className="form-select"
                disabled={saving}
              >
                <option value="">Selecione...</option>
                {maquinas.map(m => (
                  <option key={m.id} value={m.id}>{m.descricao}</option>
                ))}
              </select>
            </div>

            <div className="col-lg-3 col-md-6">
              <label className="form-label fw-semibold">Operador</label>
              <input
                type="text"
                name="operador"
                value={form.operador}
                onChange={handleChange}
                className="form-control"
                disabled={saving}
              />
            </div>

            <div className="col-md-9">
              <label className="form-label fw-semibold">Observação</label>
              <input
                type="text"
                name="observacao"
                value={form.observacao}
                onChange={handleChange}
                className="form-control"
                disabled={saving}
              />
            </div>

            <div className="col-md-3 d-flex align-items-end justify-content-end">
              <button
                type="submit"
                className="btn btn-success w-100 d-flex align-items-center justify-content-center gap-2"
                disabled={saving}
              >
                <i className="bi bi-save" />
                <span>{saving ? 'Salvando...' : 'Salvar'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card shadow-lg border-0 rounded-3">
        <div className="card-header bg-light d-flex justify-content-between align-items-center">
          <h6 className="mb-0 text-success fw-bold">Histórico de Aplicações</h6>
          <button className="btn btn-outline-success btn-sm" onClick={carregarAplicacoes} disabled={loading}>
            <i className="bi bi-arrow-clockwise me-1" />
            Atualizar
          </button>
        </div>

        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-success" role="status" />
              <p className="mt-2 mb-0">Carregando aplicações...</p>
            </div>
          ) : aplicacoes.length === 0 ? (
            <div className="alert alert-info mb-0">
              Nenhuma aplicação de insumo cadastrada.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-success">
                  <tr>
                    <th>Safra</th>
                    <th>Insumo</th>
                    <th>Talhão</th>
                    <th>Máquina</th>
                    <th>Quantidade</th>
                    <th>Data</th>
                    <th>Observação</th>
                    <th className="text-end">Ações</th>
                  </tr>
                </thead>

                <tbody>
                  {aplicacoes.map(item => (
                    <tr key={item.id}>
                      <td>{item.safraNome || item.safraId}</td>
                      <td>{item.insumoNome || item.insumoId}</td>
                      <td>{item.talhaoNome || '-'}</td>
                      <td>{item.maquinaNome || '-'}</td>
                      <td>{item.quantidade}</td>
                      <td>
                        {item.dataAplicacao
                          ? new Date(item.dataAplicacao).toLocaleDateString('pt-BR')
                          : '-'}
                      </td>
                      <td>{item.observacao || '-'}</td>
                      <td className="text-end">
                        <button
                          className="btn btn-outline-primary btn-sm me-2"
                          onClick={() => navigate(`/aplicacoesinsumo/editar/${item.id}`)}
                          disabled={loading}
                          title="Editar"
                        >
                          <i className="bi bi-pencil-square" />
                        </button>

                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => handleDelete(item.id)}
                          disabled={loading}
                          title="Excluir"
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AplicacaoInsumoList;
